import React, { useEffect } from "react";
import { HiMiniXMark } from "react-icons/hi2";
import { useNavigate, useParams } from "react-router-dom";

import { RecentSearchIcon } from "../../../assets/svg";

import { getGlobalSearch, publicRewardsData } from "../../../Services/APIs/LandingPage";

import { backgroundColor, defaultTextColor, hexToRgb } from "../../../Utils/CommonFunctions";
import { getOfferTypeResult } from "../../../Utils/Constant";

import Spinner from "../Spinner";

import { useAuth } from "../../../hoc/AuthContext";
import useWindowSize from "../../../hooks/useWindowSize";

const saveRecentSearch = (searchTerm, setRecentSearchesState) => {
  const recentSearches = JSON.parse(localStorage.getItem('recentSearches')) || [];
  // Remove the searchTerm if it already exists to avoid duplicates
  const filteredSearches = recentSearches.filter(term => term !== searchTerm);

  // Add the new search term to the beginning of the array
  filteredSearches.unshift(searchTerm);

  // Store only the last 5 searches
  const updatedSearches = filteredSearches.slice(0, 5);

  // Save back to localStorage
  localStorage.setItem('recentSearches', JSON.stringify(updatedSearches));

  // Update state
  setRecentSearchesState(updatedSearches);
};

// Function to group search results by brand
const groupByBrand = (data) => {
  if (!Array.isArray(data)) return []; // Return empty array if data is not an array

  const brandMap = {};
  data.forEach((item) => {
    const brandId = item.brand.id;
    if (!brandMap[brandId]) {
      brandMap[brandId] = {
        brand: {
          id: item.brand.id,
          name: item.brand.name,
          logo: item.brand.logo,
          hex_code: item.brand.hex_code,
          text_hex_code: item.brand.text_hex_code,
          brand_category: item.brand.brand_category,
        },
        offers: [],
      };
    }
    brandMap[brandId].offers.push({
      id: item.id,
      title: item.title,
      saving_amount: item.saving_amount,
      region: item.region,
      offer_category: item.offer_category,
      expiry: item.expiry,
      redemption_channel: item.redemption_channel,
      slug: item.slug,
      offer_type: item?.offer_type,
      brand: item?.brand,
    });
  });

  return Object.values(brandMap);
};

export default function ShowSearchBarResult({ searchLoading, searchresult, resultsRef, recentSearchesState, setRecentSearchesState, showSearchBar, setShowSearchBar, searchTerm, SetSearchTerm, setSearchResult, setSearchLoading, setShowSearchBarMobile, setShowMenuMobile, PublicPage }) {
  const { isMobile } = useWindowSize();
  const searchresultData = searchresult?.length > 0 ? searchresult : "No exact matches found";
  const navigate = useNavigate();
  const { isAuthenticated, setShowLogin, setShowSignUp, setCardInfo, subVarientIdGlobal, integratorTheme, setWindowLocationUrl } = useAuth();
  const { integratorId, subId } = useParams();

  const handleInputChange = (event) => {
    SetSearchTerm(event.target.value);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!searchTerm || searchTerm?.length < 3) return; // Avoid empty search terms or length minimum 
      setSearchLoading(true);
      try {
        if (PublicPage) {
          const searchparam = `offer_types=['merchant_funded_offers', 'affiliate_cashback', 'access_n_membership', 'gift_card']&search=${searchTerm}&page=1&limit=10&card_id=${subId || subVarientIdGlobal}`;
          const PublicResult = await publicRewardsData(searchparam);
          if (PublicResult?.status === 200) {
            setSearchResult(PublicResult?.data?.result);
          }
        } else {
          const param = `query=${searchTerm}`;
          const result = await getGlobalSearch(param);
          if (result?.status === 200) {
            setSearchResult(result?.data);
          }
        }
      } catch (e) {
        console.log(e?.data?.error);
      } finally {
        setSearchLoading(false);
      }
    };

    // Debouncing: wait for 3 seconds after the user stops typing
    const timer = setTimeout(() => {
      fetchData();
    }, 300);

    // Clean up the timeout when searchTerm changes or component unmounts
    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Detect click outside of search popup
  useEffect(() => {
    function handleClickOutside(event) {
      if (resultsRef.current && !resultsRef.current.contains(event.target)) {
        if (isMobile) {
          setShowSearchBarMobile(false);
          setShowMenuMobile(false);
        }
        setShowSearchBar(false);
      }
    }

    // Attach event listener when search bar is open
    if (showSearchBar) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      // Cleanup event listener when component unmounts or search bar closes
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSearchBar]);

  const handleOfferTypeClick = (type, id, name, link) => {
    SetSearchTerm('');
    saveRecentSearch(name, setRecentSearchesState); // Save recent search for all users
    // const currentPath = window.location.pathname + window.location.search;
    if (type === "publicSearch") {
      setShowMenuMobile(false);
      setShowSearchBarMobile(false);
      setShowSearchBar(false);
      SetSearchTerm('');
      setSearchResult(null);
      navigate(link);
      window.location.reload();
      return
    }

    if (type === 'recent_search') {
      SetSearchTerm(name);
      return;
    }

    // Construct the intended URL based on the search result
    let path = PublicPage ? `/rewards/${integratorId}/${subId || subVarientIdGlobal}` : '/rewards';
    const queryParams = new URLSearchParams();

    if (isAuthenticated) {
      queryParams.set('card_id', subVarientIdGlobal);
    }
    queryParams.delete('filters');
    queryParams.delete('offer_types');

    switch (type) {
      case 'offer_category':
        queryParams.set('offer_types', JSON.stringify(['merchant_funded_offers', 'affiliate_cashback', 'access_n_membership', 'gift_card']));
        queryParams.set('filters', JSON.stringify({ offer_categories: [id] }));
        break;
      case 'brand_category':
        queryParams.set('offer_types', JSON.stringify(['merchant_funded_offers', 'affiliate_cashback', 'access_n_membership', 'gift_card']));
        queryParams.set('filters', JSON.stringify({ brand_categories: [id] }));
        break;
      case 'offer_type':
        queryParams.set('offer_types', JSON.stringify([id]));
        break;
      case 'brand':
        queryParams.set('offer_types', JSON.stringify(['merchant_funded_offers', 'affiliate_cashback', 'access_n_membership', 'gift_card']));
        queryParams.set('filters', JSON.stringify({ brands: [id] }));
        break;
      default:
        return;
    }

    const intendedUrl = `${path}?${queryParams.toString()}`;

    if (!isAuthenticated) {
      setWindowLocationUrl(intendedUrl); // Save the intended URL
      setShowLogin(true);
      setShowSignUp(false);
      let newData = {
        search: true,
        url: intendedUrl,
      };
      setCardInfo(newData);
    } else {
      navigate(intendedUrl, {
        state: {
          fromSearch: true,
          isCardUrl: true,
          cardURL: type === 'offer_type' ? id : 'all_rewards',
        },
      });
    }

    // Reset all search-related states
    setShowMenuMobile(false);
    setShowSearchBarMobile(false);
    setShowSearchBar(false);
    SetSearchTerm('');
    setSearchResult(null);
  };

  const recentSearches = JSON.parse(localStorage.getItem('recentSearches')) || [];

  // Function to remove a specific search term from localStorage and update state
  const removeSearchFromLocalStorage = (searchToRemove) => {
    const updatedSearches = recentSearches.filter(search => search !== searchToRemove);

    localStorage.setItem('recentSearches', JSON.stringify(updatedSearches));
    setRecentSearchesState(updatedSearches);
  };

  // Helper function to display the correct label for offer types
  const getOfferLabel = (search) => {
    switch (search) {
      case 'merchant_funded_offers':
        return 'Discount Offers';
      case 'affiliate_cashback':
        return 'Cashback';
      case 'gift_card':
        return 'Gift Cards';
      case 'access_n_membership':
        return 'Access & Membership';
      default:
        return search;
    }
  };

  const [r, g, b] = hexToRgb(backgroundColor(integratorTheme));
  const backgroundColorRecent = `rgba(${r}, ${g}, ${b}, 0.8)`;

  function renderPublicSearchResult() {
    const searchresultData = Array.isArray(searchresult) ? searchresult : [];
    const groupedData = groupByBrand(searchresultData);

    return (
      <>
        {groupedData?.length > 0 ? (
          <div className="flex flex-col max-h-[200px] md:max-h-[300px] min-w-[100px] md:min-h-[200px] h-full overflow-auto w-full gap-4">
            {/* <div className="font-semibold text-lg">{searchTerm}</div> */}
            {groupedData?.map((brandData) => (
              <div key={brandData?.brand?.id} className="flex flex-col gap-2 md:gap-4">
                {/* Brand Header */}
                <div className="flex items-center gap-3">
                  <img
                    src={brandData?.brand?.logo}
                    alt={brandData?.brand?.name}
                    className="w-12 h-12 object-contain"
                    loading="lazy"
                  />
                  <div className="font-semibold text-base md:text-lg">{brandData?.brand?.name}</div>
                </div>
                {/* Offers List */}
                <div className="flex flex-col gap-3 pl-2.5 md:pl-4">
                  {brandData.offers.map((offer, offerIndex) => {
                    const BrandName = offer?.brand?.name?.replace(/ /g, "-");
                    const link = `/rewards/${BrandName}/${integratorId}/${subId}/${offer?.id}`;
                    return (
                      <div
                        key={offer.id}
                        className="flex flex-col md:flex-row gap-1 border-l-2 justify-between border-gray-200 pl-2 md:pl-3 items-start md:items-center"
                        onClick={() => handleOfferTypeClick("publicSearch", null, offer?.brand?.name, link)}
                      >
                        <div className="flex items-center gap-1">
                          {/* <span className="text-sm">Offer - {offerIndex + 1}</span> */}
                          {/* <span className="text-sm md:text-base max-w-[150px] md:truncate">
                            {getOfferTypeResult(offer?.offer_type)?.name} - {offerIndex + 1}
                          </span> */}
                          <div title={offer?.saving_amount} className="text-sm md:text-base md:max-w-[250px] md:truncate"> {offer?.saving_amount}</div>
                        </div>
                        <div className="flex items-center gap-2.5">
                          <div style={{
                            color: defaultTextColor(integratorTheme),
                          }} className="text-[10px] md:text-sm p-1 px-2 bg-[#fff] rounded-md">{offer?.region?.name}</div>
                          <div style={{
                            color: defaultTextColor(integratorTheme),
                          }} className="text-[10px] md:text-sm p-1 px-2 bg-[#fff] rounded-md">{getOfferTypeResult(offer?.offer_type)?.title}</div>
                          {/* <div style={{ color: defaultTextColor(integratorTheme) }} className="text-[10px] md:text-sm p-1 px-2 bg-[#fff] rounded-md">{offer?.offer_category?.name}</div> */}
                        </div>
                      </div>
                    )
                  }
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (searchTerm?.length > 0 ?
          <div className="w-full text-left text-base">
            No results found for "{searchTerm}"
          </div> : ""
          // <div className="">No exact matches found</div>
        )}
      </>
    )
  };

  function renderPrivateSearchResult() {
    const searchresultData = Array.isArray(searchresult) ? searchresult : [];
    return (
      <>
        {Array.isArray(searchresultData) && searchresultData.length > 0 ? (
          <div className="flex flex-col w-full gap-4">
            {searchresultData.map((result, index) => {
              // Extract the icon component
              const OfferType = getOfferTypeResult(result?.name)?.Icon;

              return (
                <div key={index}>
                  {result?.source === "brand" ? (
                    <div className="p-4 rounded-md flex justify-between items-center gap-2.5 hover:bg-[#cfcfcfe3] bg-[#ebebeb] cursor-pointer w-full"
                      onClick={() => handleOfferTypeClick('brand', result?.id, result?.name)}
                    >
                      <img
                        className="w-12 h-12 rounded-[4px] object-contain"
                        style={{ backgroundColor: `#${result?.hex_code}` }}
                        src={result?.logo}
                        alt={result?.name}
                      />
                      <div className="flex flex-col w-full">
                        <div className="text-base font-semibold">{result?.name}</div>
                        {result?.tag_line && (
                          <div style={{ color: defaultTextColor(integratorTheme) }} className="text-sm">{result?.tag_line}</div>
                        )}
                      </div>
                    </div>
                  ) : result?.source === "offer_type" ? (
                    <div
                      className="p-4 rounded-md flex items-center gap-2.5 hover:bg-[#cfcfcfe3] bg-[#ebebeb] cursor-pointer w-full hover:fill-[#F5437E]"
                      onClick={() => handleOfferTypeClick('offer_type', result?.name, result?.name)}
                    >
                      {OfferType && <OfferType />}
                      <div className="text-base font-semibold">
                        {getOfferTypeResult(result?.name)?.title}
                      </div>
                    </div>
                  ) : result?.source === "offer_category" ? (
                    <div
                      className="p-4 rounded-md flex items-center gap-2.5 hover:bg-[#cfcfcfe3] bg-[#ebebeb] cursor-pointer w-full hover:fill-[#F5437E]"
                      onClick={() => handleOfferTypeClick('offer_category', result?.id, result?.name)}
                    >
                      <div className="text-base font-semibold">
                        Spend Type - {result?.name}
                      </div>
                    </div>
                  ) : result?.source === "brand_category" ? (
                    <div
                      className="p-4 rounded-md flex items-center gap-2.5 hover:bg-[#cfcfcfe3] bg-[#ebebeb] cursor-pointer w-full"
                      onClick={() => handleOfferTypeClick('brand_category', result?.id, result?.name)}
                    >
                      {result?.bg_image ? (
                        <img className={`w-12 h-12 object-contain bg-gray-300 rounded-full`}
                          src={result?.bg_image} alt={result?.name} />
                      ) : <div className="w-12 h-12 bg-gray-300 rounded-full" />
                      }
                      <div className="text-base font-semibold">{result?.name} </div>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="w-full text-left text-base">
            {searchresultData}
          </div>
        )}
      </>
    )
  }

  return (showSearchBar &&
    <>
      <div className="fixed top-0 z-40 w-full h-full bg-black opacity-70"></div>
      <div className="fixed w-full h-full top-10 z-50 flex items-start justify-center p-4 font-Inter" onClick={(e) => e.stopPropagation()}>
        <div className="absolute z-20 border rounded-xl bg-[#f7f7f7] p-4 max-w-[600px] w-full left-1/2 -translate-x-1/2" ref={resultsRef}>
          <div className="flex w-full justify-between mb-2">
            <div className=" font-semibold text-xl">Search</div>
            <HiMiniXMark color="#3B3E63" size={26}
              onClick={() => {
                if (isMobile) {
                  setShowMenuMobile(false);
                  setShowSearchBarMobile(false);
                }
                SetSearchTerm('');
                setShowSearchBar(false)
              }}
              className={"cursor-pointer"}
            />
          </div>
          <div className="border flex gap-4 items-center bg-[#F7F7F7] border-[#E5E5E5] rounded-[10px] w-full px-4 mx-auto">
            <input
              type="text"
              value={searchTerm}
              onChange={handleInputChange}
              placeholder="Search for brand, offers etc."
              className="placeholder-[#3B3E63] bg-[#F7F7F7] font-Inter placeholder:font-Inter text-sm border-0 p-0 w-full h-10 focus:border-transparent focus:outline-none focus:ring-0 placeholder:text-sm appearance-none"
            />
          </div>
          <div
            className="relative w-full mt-4 left-1/2 -translate-x-1/2 flex-col searchBar border z-20 flex gap-4 items-center bg-[#F7F7F7] border-[#E5E5E5] rounded-[8px] p-4"
          >
            {searchLoading ? <div className="flex justify-center items-center w-full"><Spinner /></div> : (
              PublicPage ? renderPublicSearchResult() : renderPrivateSearchResult()
            )}

            {recentSearchesState.length > 0 && (
              <div className="w-full">
                <h3 className="font-semibold">Recent Searches</h3>
                <ul className="flex flex-wrap gap-2 mt-2">
                  {recentSearchesState.map((search, index) => (
                    <div
                      key={index}
                      className="p-2 cursor-pointer items-center rounded-[20px] bg-[#fff] hover:bg-[#cdcdcd] flex justify-between gap-1"
                      style={{ color: defaultTextColor(integratorTheme) }}
                    >
                      <div className="flex gap-2.5 items-center text-sm md:text-base" onClick={() => handleOfferTypeClick('recent_search', null, getOfferLabel(search))}>
                        <RecentSearchIcon style={{ fill: defaultTextColor(integratorTheme) }} className="w-4 h-4" />
                        {getOfferLabel(search)}
                      </div>
                      <HiMiniXMark className="mt-1" onClick={() => removeSearchFromLocalStorage(search)} />
                    </div>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}